import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/d9q86ffnahb7382ov7fg';

const reputationItems = [
  {
    icon: 'ri-shield-check-line',
    title: '严格筛选',
    desc: '每一部推荐作品均经过团队深度评测，从剧情、制作、口碑等多维度综合考量，确保推荐质量。',
  },
  {
    icon: 'ri-eye-line',
    title: '公开透明',
    desc: '评分标准公开透明，每部作品的推荐理由清晰明确，绝不盲目跟风推荐。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    desc: '以观众体验为核心，关注不同类型观众的偏好，提供个性化的观看建议。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续更新',
    desc: '关注动漫行业动态，定期更新推荐列表，确保内容的新鲜度和时效性。',
  },
];

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {t('breadcrumb.home')}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">{t('about.title')}</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              {t('about.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-2xl">
              {t('about.subtitle')} - {t('about.intro')}
            </p>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                  {t('about.mission')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t('about.mission.text')}
                </p>
              </div>
              <div className="rounded-lg overflow-hidden h-[260px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20workspace%20with%20anime%20posters%20on%20the%20wall%2C%20a%20wooden%20desk%20with%20a%20laptop%20and%20notebooks%2C%20soft%20natural%20light%20from%20a%20window%2C%20creative%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20editorial%20photography&width=600&height=400&seq=about-mission&orientation=landscape"
                  alt="我们的使命"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
              {t('about.reputation')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {reputationItems.map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <i className={`${item.icon} text-2xl text-accent-500`}></i>
                  </div>
                  <h4 className="font-heading font-bold text-foreground-900 text-sm mb-2">{item.title}</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
              {t('about.contact')}
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8 max-w-xl mx-auto">
              {t('about.contact.desc')}
            </p>

            <div className="max-w-lg mx-auto">
              <form
                id="contact-form"
                data-readdy-form=""
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70"
              >
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入你的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入你的留言（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none h-0 w-0"
                  />

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-md font-medium text-sm transition-colors disabled:opacity-60 whitespace-nowrap"
                  >
                    {submitting ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">感谢你的留言！我们会尽快回复。</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}